import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js";
import Features from "../../../../Components/Features/ThreeColumnPages";
import {
  FaVideo,
  FaStar,
  FaRocket,
  FaEye,
  FaUsers,
  FaCheckCircle,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

export const RealEstateVideoMarketingInDubai = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="Real Estate Video Marketing in Dubai" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Showcase Your Properties</Subheading>}
        heading={
          <Heading>
            Real Estate <span tw="text-primary-500">Video Marketing</span> in
            Dubai
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, we provide professional real estate video
            marketing services in **Dubai**, creating cinematic property
            showcases, virtual tours, and promotional videos that captivate
            buyers and investors. Highlight iconic locations like Palm Jumeirah,
            Dubai Marina, and Downtown Dubai to drive engagement and sales.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1560184897-ae75f418493e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Modern Dubai property
          "https://images.unsplash.com/photo-1582610116397-edb318620f90?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Luxury apartment in Dubai
          "https://images.unsplash.com/photo-1600607687644-c7f34b5063c7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Elegant interior design
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Showcase Your Property"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Expert Real Estate Video Marketing in Dubai"
        description="We produce videos that sell properties faster."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Cinematic Showcases",
            description:
              "Stunning visuals for villas, apartments, and developments.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Proven Success",
            description: "Trusted by Dubai’s top real estate firms.",
          },
          {
            icon: <FaRocket color="#ff5464" size={40} />,
            title: "Fast Turnaround",
            description: "Quick delivery for time-sensitive listings.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Buyer Engagement",
            description: "Videos designed to attract and convert.",
          },
          {
            icon: <FaUsers color="#ff5464" size={40} />,
            title: "Tailored Content",
            description: "Customized for projects like Dubai Hills Estate.",
          },
          {
            icon: <FaCheckCircle color="#ff5464" size={40} />,
            title: "Multi-Platform Ready",
            description: "Optimized for websites, social media, and MLS.",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our Real Estate Video Marketing Process"
        description={
          <Description>
            We craft impactful real estate videos with a streamlined process:
            <ul tw="mt-4 list-disc pl-6">
              <li>
                Consultation: Understand your property and marketing goals.
              </li>
              <li>
                Planning: Develop scripts and storyboards for maximum appeal.
              </li>
              <li>
                Filming: Use 4K cameras and drones for breathtaking visuals.
              </li>
              <li>
                Editing: Enhance with motion graphics, music, and voiceovers.
              </li>
              <li>Delivery: Provide polished videos for immediate use.</li>
              <li>Support: Offer revisions and performance analytics.</li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Property interior view
          "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Modern building exterior
          "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Stylish apartment living room
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Real Estate Video Marketing Wins in Dubai"
        description="Our videos have accelerated property sales."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Luxury Villa Sale",
            description: "Sold a Palm Jumeirah villa with a viral video.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Project Launch",
            description: "Boosted inquiries for a Dubai Marina development.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Client Satisfaction",
            description: "98% approval from real estate clients.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready to Sell Faster?</Subheading>}
        heading="Real Estate Video Marketing in Dubai"
        description={
          <Description>
            Partner with MilaajBrandset for professional real estate video
            marketing services in Dubai. Get cinematic, high-impact videos that
            showcase your properties and drive results—contact us today!
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Luxury villa
          "https://images.unsplash.com/photo-1600210492493-0946911123ea?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Modern kitchen design
          "https://images.unsplash.com/photo-1464938050520-ef2270bb8ce8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Dubai skyline with property
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Now"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
